import { useState } from 'react'
import { UploadRSS } from './UploadRSS'
import { UploadYouTube } from './UploadYouTube'
import { UploadFile } from './UploadFile'
import usePodcastFormStepsStore from 'store/usePodcastFormStepsStore'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { faUpload, faPodcast, faPlay } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

function SelectType({ onTypeSelect }) {
  const { submission, updateSubmission } = usePodcastFormStepsStore()

  const handleSelectChange = (value) => {
    updateSubmission({
      ...submission,
      upload_method: value,
    })
    onTypeSelect(value)
  }

  const items: {
    name: string
    description: string
    iconColor: string
    icon: JSX.Element
    value: string
    disabled?: boolean
  }[] = [
    {
      name: 'Upload a File (slower)',
      description: 'We support virtually every video and audio format.',
      iconColor:
        submission.upload_method === 'upload' || !submission.upload_method
          ? 'bg-blue-500'
          : 'bg-gray-200',
      icon: <FontAwesomeIcon icon={faUpload} color="white" />,
      value: 'upload',
    },
    {
      name: 'By RSS Feed (fast)',
      description: 'Copy and paste the link of your podcast RSS feed.',
      iconColor:
        submission.upload_method === 'rss' ? 'bg-purple-500' : 'bg-gray-200',
      icon: <FontAwesomeIcon icon={faPodcast} color="white" />,
      value: 'rss',
    },
    {
      name: 'Import by URL',
      description:
        'Copy and paste a link from YouTube, Reddit, TikTok, Instagram, Facebook, or Twitter/X and we will do the rest.',
      iconColor:
        submission.upload_method === 'youtube' ? 'bg-blue-500' : 'bg-gray-200',
      icon: <FontAwesomeIcon icon={faPlay} color="white" />,
      value: 'youtube',
      // disabled: true,
    },
  ]

  return (
    <div className="mx-auto max-w-5xl">
      <ul role="list" className="mt-6">
        {items.map((item, itemIdx) => (
          <li
            key={itemIdx}
            onClick={() => {
              if (!item.disabled) {
                handleSelectChange(item.value)
              }
            }}
            className={classNames(
              item.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              ''
            )}
          >
            <div
              className={classNames(
                'group relative mb-2 flex items-start space-x-3 rounded py-4 px-4 ring-1 ring-gray-200 hover:ring-2 hover:ring-blue-500 sm:px-6',
                submission.upload_method === item.value ||
                  (!submission.upload_method && item.value === 'upload')
                  ? 'ring-2 ring-blue-500'
                  : 'ring-1 ring-gray-200',
                item.disabled ? 'opacity-50' : ''
              )}
            >
              <div className="flex-shrink-0">
                <span
                  className={classNames(
                    item.iconColor,
                    'inline-flex h-10 w-10 items-center justify-center rounded-lg'
                  )}
                >
                  {item.icon}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900">
                  {item.name} {item.disabled && '(Temporarily Disabled)'}
                </div>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
              <div className="flex-shrink-0 self-center">
                <ChevronRightIcon
                  className="h-5 w-5 text-gray-400 group-hover:text-gray-500"
                  aria-hidden="true"
                />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

export const Upload = ({ onUpload }) => {
  const { submission } = usePodcastFormStepsStore()
  const [uploadType, setUploadType] = useState(
    submission.upload_method || 'upload'
  )

  const renderUploadType = (type) => {
    switch (type) {
      case 'rss':
        return <UploadRSS onUpload={onUpload} />
      case 'youtube':
        return <UploadYouTube onUpload={onUpload} />
      default:
        return <UploadFile onUpload={onUpload} />
    }
  }

  return (
    <>
      <SelectType onTypeSelect={setUploadType} />
      {renderUploadType(uploadType)}
    </>
  )
}
